import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

// Shared navigation state: mobile/tablet drawer (<1024px) + remembered desktop collapse.
const DESKTOP_QUERY = '(min-width: 1024px)';
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Survives the layout remount that happens on a route change.
let pendingMainFocus = false;
// Bumped whenever the drawer opens so an older pending focus can't steal focus later.
let focusToken = 0;

const readPref = (key) => {
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
};

// Moves focus to the new page's main heading (waits briefly for async pages), else to <main>.
function focusMainContent(main) {
  const token = ++focusToken;
  let timer;
  let done = false;
  let tries = 0;
  const attempt = () => {
    if (token !== focusToken || !main?.isConnected || main.contains(document.activeElement)) { done = true; return; }
    const heading = main.querySelector('h1');
    if (!heading && tries < 20) { tries += 1; timer = setTimeout(attempt, 100); return; }
    const target = heading || main;
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus();
    done = true;
  };
  timer = setTimeout(attempt, 0);
  return () => { clearTimeout(timer); if (!done) pendingMainFocus = true; };
}

export default function useResponsiveNav(storageKey, focusKey) {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(DESKTOP_QUERY).matches);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => readPref(storageKey));
  const drawerRef = useRef(null);
  const menuButtonRef = useRef(null);
  const mainRef = useRef(null);
  const wasOpen = useRef(false);
  const returnFocus = useRef(true);
  const mobileOpen = drawerOpen && !isDesktop;
  const mobileOpenRef = useRef(mobileOpen);
  mobileOpenRef.current = mobileOpen;

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e) => {
      setIsDesktop(e.matches);
      if (e.matches) setDrawerOpen(false);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(storageKey, next ? '1' : '0'); } catch { /* storage unavailable */ }
      return next;
    });
  }, [storageKey]);

  const openDrawer = useCallback(() => {
    focusToken += 1;
    pendingMainFocus = false;
    // The post-login welcome toast would otherwise sit over the top of the open drawer.
    toast.dismiss('welcome');
    setDrawerOpen(true);
  }, []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  // Use when a drawer item navigates elsewhere: focus goes to the new page, not back to Menu.
  const closeAfterNavigate = useCallback(() => {
    if (mobileOpenRef.current) {
      pendingMainFocus = true;
      returnFocus.current = false;
    }
    setDrawerOpen(false);
  }, []);

  useEffect(() => {
    if (!pendingMainFocus) return undefined;
    pendingMainFocus = false;
    return focusMainContent(mainRef.current);
  }, [focusKey]);

  useEffect(() => {
    if (!mobileOpen) {
      if (wasOpen.current) {
        wasOpen.current = false;
        if (returnFocus.current) menuButtonRef.current?.focus();
      }
      returnFocus.current = true;
      return undefined;
    }
    wasOpen.current = true;
    const drawer = drawerRef.current;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusables = () => Array.from(drawer?.querySelectorAll(FOCUSABLE) || [])
      .filter((el) => el.getClientRects().length > 0);
    // Small delay: the drawer only becomes focusable once its visibility transition starts.
    const timer = setTimeout(() => focusables()[0]?.focus(), 60);

    const onKey = (e) => {
      const active = document.activeElement;
      // Let open popovers (e.g. the user menu) handle their own keys first.
      if (active?.closest?.('[role="menu"]')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        setDrawerOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (!drawer.contains(active)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileOpen]);

  return {
    isDesktop,
    drawerOpen: mobileOpen,
    openDrawer,
    closeDrawer,
    closeAfterNavigate,
    collapsed: isDesktop && collapsed,
    toggleCollapsed,
    drawerRef,
    menuButtonRef,
    mainRef,
    navHidden: !isDesktop && !drawerOpen,
  };
}
